import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Tracks coverage data changes to detect cells that just got filled,
 * departments that reached 100%, and session-level stats.
 *
 * @param {Array} timelineData - Raw timeline data from API
 * @returns {{ justFilled: Map, sessionStats: Object, completedDepts: Set }}
 */
export default function useCoverageAnimations(timelineData) {
  const prevDataRef = useRef(null);
  const [justFilled, setJustFilled] = useState(new Map());
  const [completedDepts, setCompletedDepts] = useState(new Set());
  const sessionStats = useRef({ gapsFilled: 0, posScraped: 0, startedAt: Date.now() });

  // Diff timeline data on each refresh
  useEffect(() => {
    const timeline = Array.isArray(timelineData) ? timelineData : [];
    const prev = prevDataRef.current;
    if (!prev || timeline.length === 0) {
      prevDataRef.current = timeline;
      return;
    }

    const newFills = new Map();
    const newCompleted = new Set();

    for (const dept of timeline) {
      const prevDept = prev.find(d => d.department_id === dept.department_id);
      if (!prevDept) continue;

      const prevMonths = {};
      for (const m of (prevDept.months || [])) {
        const key = m.month?.substring(0, 7);
        if (key) prevMonths[key] = m;
      }

      for (const m of (dept.months || [])) {
        const mk = m.month?.substring(0, 7);
        if (!mk) continue;
        const prevMonth = prevMonths[mk];

        for (const type of ['IT Goods', 'IT Services']) {
          const newPos = m.acq_types?.[type]?.pos || 0;
          const oldPos = prevMonth?.acq_types?.[type]?.pos || 0;

          if (newPos > 0 && oldPos === 0) {
            const key = `${mk}:${type}`;
            newFills.set(key, { pos: newPos, filledAt: Date.now() });
            sessionStats.current.gapsFilled++;
            sessionStats.current.posScraped += newPos;
          }
        }
      }
    }

    if (newFills.size > 0) {
      setJustFilled(prev => {
        const merged = new Map(prev);
        for (const [k, v] of newFills) merged.set(k, v);
        return merged;
      });

      // Fire notifications for departments that just hit 100% for a type
      for (const dept of timeline) {
        const prevDept = prev.find(d => d.department_id === dept.department_id);
        if (!prevDept) continue;

        for (const type of ['IT Goods', 'IT Services']) {
          const allMonths = (dept.months || []).filter(m => m.month);
          const filledNow = allMonths.filter(m => (m.acq_types?.[type]?.pos || 0) > 0).length;
          const prevFilled = (prevDept.months || []).filter(m => m.month && (m.acq_types?.[type]?.pos || 0) > 0).length;

          if (filledNow > prevFilled && filledNow === allMonths.length && allMonths.length > 0) {
            const deptKey = `${dept.department_id}:${type}`;
            newCompleted.add(deptKey);
            if (window.addNotification) {
              window.addNotification({
                type: 'scraper',
                message: `${dept.acronym || dept.department_name} ${type} — 100% Complete!`,
              });
            }
          }
        }
      }

      if (newCompleted.size > 0) {
        setCompletedDepts(prev => {
          const merged = new Set(prev);
          for (const k of newCompleted) merged.add(k);
          return merged;
        });
        // Clear completed state after 5s
        setTimeout(() => {
          setCompletedDepts(prev => {
            const next = new Set(prev);
            for (const k of newCompleted) next.delete(k);
            return next;
          });
        }, 5000);
      }

      // Clear justFilled entries after 5s
      setTimeout(() => {
        setJustFilled(prev => {
          const next = new Map(prev);
          for (const k of newFills.keys()) next.delete(k);
          return next;
        });
      }, 5000);
    }

    prevDataRef.current = timeline;
  }, [timelineData]);

  const getSessionStats = useCallback(() => ({ ...sessionStats.current }), []);

  return { justFilled, completedDepts, getSessionStats };
}
